import React from 'react';
import { CalendarClock, ChevronDown } from 'lucide-react';

export interface VisiteMission {
    id: string; date: string; heure: string; label: string; status: string;
    payment_status: string; equipe: number; total_ttc: number;
}

// Carte « Visites » du suivi de prestation (2026-10-16, refonte demandée par Emeni, inspirée de Jobber : une mission,
// plusieurs visites). Lecture seule : elle résume l'avancement (« 2 visites sur 5 faites »), l'état de chaque visite et
// ce qui est payé. La planification reste dans « Planifier » ci-dessous — rien n'est recopié ici.

const ETATS: Record<string, { label: string; classe: string }> = {
    termine: { label: 'Terminée', classe: 'bg-green-500/10 text-green-600' },
    en_cours: { label: 'En cours', classe: 'bg-brand-500/15 text-brand-600' },
    planifie: { label: 'Planifiée', classe: 'bg-surface-soft text-content-muted' },
    demande: { label: 'À confirmer', classe: 'bg-warning-500/10 text-warning-600' },
    attente_paiement: { label: 'En attente de paiement', classe: 'bg-slate-500/10 text-slate-500' },
};

const eur = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const aujourdhui = () => new Date().toISOString().slice(0, 10);

export const VisitesMission: React.FC<{ visites: VisiteMission[]; totalDevis?: number }> = ({ visites, totalDevis = 0 }) => {
    // Repliée par défaut : « 1 sur 2 faite » se lit d'un coup d'œil, le détail s'ouvre d'un clic (2026-10-19, demande d'Emeni : écrans épurés).
    const [ouverte, setOuverte] = React.useState(false);
    if (!visites || visites.length === 0) return null;
    const faites = visites.filter((v) => v.status === 'termine').length;
    const part = Math.round((faites / visites.length) * 100);
    const totalVisites = visites.reduce((somme, v) => somme + (v.total_ttc || 0), 0);
    // Tant qu'aucune visite n'est terminée, le montant d'un rendez-vous planifié ne contient pas ce qui s'ajoute à la clôture
    // (frais kilométriques, options) : le total annoncé au client est celui du devis, on ne montre jamais moins.
    const total = faites === 0 && totalDevis > totalVisites ? totalDevis : totalVisites;
    const paye = visites.filter((v) => v.payment_status === 'encaisse').reduce((somme, v) => somme + (v.total_ttc || 0), 0);
    const jour = aujourdhui();
    return (
        <section className="bg-surface border border-border rounded-2xl p-5 space-y-3" aria-label="Visites de la mission">
            <button type="button" onClick={() => setOuverte((o) => !o)} aria-expanded={ouverte} className="w-full flex items-center justify-between gap-3 flex-wrap text-left">
                <h3 className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={16} /> Visites <span className="font-normal text-content-muted">· {faites} sur {visites.length} faite{faites > 1 ? 's' : ''}</span></h3>
                <div className="h-1.5 w-36 rounded-full bg-surface-soft overflow-hidden" role="progressbar" aria-valuenow={part} aria-valuemin={0} aria-valuemax={100}>
                    <div className="h-full bg-brand-600" style={{ width: `${part}%` }} />
                </div>
                <ChevronDown size={16} className={`text-content-muted transition-transform ${ouverte ? 'rotate-180' : ''}`} />
            </button>
            {ouverte && <>
            <ul className="divide-y divide-border">
                {visites.map((v) => {
                    const etat = v.status === 'planifie' && v.date === jour ? { label: "Aujourd'hui", classe: 'bg-brand-500/15 text-brand-600' } : (ETATS[v.status] || ETATS.planifie);
                    const d = new Date(v.date + 'T00:00:00');
                    return (
                        <li key={v.id} className="flex items-center gap-3 py-2.5">
                            <div className="w-12 shrink-0 text-center rounded-lg bg-surface-soft py-1">
                                <span className="block text-[10px] uppercase text-content-muted">{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                                <span className="block text-base font-bold text-content leading-tight">{d.getDate()}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-content truncate">{v.label}</p>
                                <p className="text-[11px] text-content-muted">{v.heure} · {v.equipe > 0 ? `${v.equipe} salarié${v.equipe > 1 ? 's' : ''}` : 'aucun salarié assigné'}</p>
                            </div>
                            <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${etat.classe}`}>{etat.label}</span>
                        </li>
                    );
                })}
            </ul>
            <div className="grid grid-cols-3 gap-2 pt-1">
                {[{ l: 'Total des visites', v: eur(total) }, { l: 'Déjà payé', v: eur(paye) }, { l: 'Reste à payer', v: eur(Math.max(0, total - paye)) }].map((m) => (
                    <div key={m.l} className="p-3 rounded-xl bg-surface-soft text-center">
                        <p className="text-sm font-bold text-content tabular-nums">{m.v}</p>
                        <p className="text-[10px] text-content-muted">{m.l}</p>
                    </div>
                ))}
            </div>
            </>}
        </section>
    );
};
